import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Progress,
  Row,
  Col,
  Card,
  Divider,
  Rate,
  Tooltip,
} from 'antd';
import {
  DeleteOutlined,
  SwapOutlined,
  StarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  HeartOutlined,
  GlobalOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { Brand } from '../services/api';

const { Title, Text } = Typography;

interface BrandComparisonProps {
  visible: boolean;
  onClose: () => void;
  brands: Brand[];
  onRemoveBrand: (brandId: number) => void;
}

interface ComparisonMetric {
  key: string;
  name: string;
  icon: React.ReactNode;
  color: string;
  getValue: (brand: Brand) => number | string;
  renderValue: (value: number | string, brand: Brand) => React.ReactNode;
}

const BrandComparison: React.FC<BrandComparisonProps> = ({
  visible,
  onClose,
  brands,
  onRemoveBrand,
}) => {
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([
    'overall_score',
    'environmental_score',
    'labor_score',
    'animal_welfare_score',
  ]);

  const comparisonMetrics: ComparisonMetric[] = [
    {
      key: 'overall_score',
      name: '综合评分',
      icon: <StarOutlined />,
      color: 'var(--app-primary-color)',
      getValue: brand => (brand as any).overall_score || 0,
      renderValue: value => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Progress
            percent={Number(value) * 20}
            size="small"
            showInfo={false}
            strokeColor="var(--app-primary-color)"
          />
          <Text strong>{Number(value).toFixed(1)}/5</Text>
        </div>
      ),
    },
    {
      key: 'environmental_score',
      name: '环境影响',
      icon: <EnvironmentOutlined />,
      color: '#52c41a',
      getValue: brand => (brand as any).environmental_score || 0,
      renderValue: value => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Progress
            percent={Number(value) * 20}
            size="small"
            showInfo={false}
            strokeColor="#52c41a"
          />
          <Text>{Number(value).toFixed(1)}/5</Text>
        </div>
      ),
    },
    {
      key: 'labor_score',
      name: '劳工权益',
      icon: <TeamOutlined />,
      color: '#faad14',
      getValue: brand => (brand as any).labor_score || 0,
      renderValue: value => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Progress
            percent={Number(value) * 20}
            size="small"
            showInfo={false}
            strokeColor="#faad14"
          />
          <Text>{Number(value).toFixed(1)}/5</Text>
        </div>
      ),
    },
    {
      key: 'animal_welfare_score',
      name: '动物福利',
      icon: <HeartOutlined />,
      color: '#eb2f96',
      getValue: brand => (brand as any).animal_welfare_score || 0,
      renderValue: value => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Progress
            percent={Number(value) * 20}
            size="small"
            showInfo={false}
            strokeColor="#eb2f96"
          />
          <Text>{Number(value).toFixed(1)}/5</Text>
        </div>
      ),
    },
    {
      key: 'community_impact_score',
      name: '社区影响',
      icon: <GlobalOutlined />,
      color: '#722ed1',
      getValue: brand => (brand as any).community_impact_score || 0,
      renderValue: value => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Progress
            percent={Number(value) * 20}
            size="small"
            showInfo={false}
            strokeColor="#722ed1"
          />
          <Text>{Number(value).toFixed(1)}/5</Text>
        </div>
      ),
    },
    {
      key: 'transparency_score',
      name: '透明度',
      icon: <EyeOutlined />,
      color: '#13c2c2',
      getValue: brand => (brand as any).transparency_score || 0,
      renderValue: value => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Progress
            percent={Number(value) * 20}
            size="small"
            showInfo={false}
            strokeColor="#13c2c2"
          />
          <Text>{Number(value).toFixed(1)}/5</Text>
        </div>
      ),
    },
    {
      key: 'founded',
      name: '成立时间',
      icon: <></>,
      color: '#666',
      getValue: brand => new Date(brand.founded).getFullYear(),
      renderValue: value => <Text>{value}年</Text>,
    },
    {
      key: 'headquarters',
      name: '总部',
      icon: <></>,
      color: '#666',
      getValue: brand => brand.headquarters,
      renderValue: value => <Text>{value}</Text>,
    },
    {
      key: 'categories',
      name: '分类',
      icon: <></>,
      color: '#666',
      getValue: brand => brand.categories.length,
      renderValue: (value, brand) => (
        <Space wrap>
          {brand.categories.map((cat: string) => (
            <Tag
              key={cat}
              style={{
                color: 'var(--app-primary-color)',
                borderColor: 'var(--app-primary-color)',
                backgroundColor: 'transparent',
              }}
            >
              {cat}
            </Tag>
          ))}
        </Space>
      ),
    },
  ];

  const getBestPerformer = (metricKey: string) => {
    if (brands.length === 0) return null;

    const metric = comparisonMetrics.find(m => m.key === metricKey);
    if (!metric) return null;

    let bestBrand = brands[0];
    let bestValue = Number(metric.getValue(bestBrand));

    brands.forEach(brand => {
      const value = Number(metric.getValue(brand));
      if (value > bestValue) {
        bestValue = value;
        bestBrand = brand;
      }
    });

    return bestBrand;
  };

  const renderComparisonTable = () => {
    const columns = [
      {
        title: '指标',
        dataIndex: 'metric',
        key: 'metric',
        width: 120,
        render: (text: string, record: any) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {record.icon}
            <Text strong>{text}</Text>
          </div>
        ),
      },
      ...brands.map(brand => ({
        title: (
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>{brand.name}</Text>
            </div>
            <Button
              size="small"
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => onRemoveBrand(brand.id)}
            >
              移除
            </Button>
          </div>
        ),
        dataIndex: `brand_${brand.id}`,
        key: `brand_${brand.id}`,
        width: 200,
        render: (value: any, record: any) => {
          const metric = comparisonMetrics.find(m => m.key === record.key);
          if (!metric) return value;

          const bestBrand = getBestPerformer(record.key);
          const isBest =
            bestBrand?.id === brand.id &&
            typeof metric.getValue(brand) === 'number' &&
            Number(metric.getValue(brand)) > 0;

          return (
            <div
              style={{
                padding: '8px',
                backgroundColor: isBest ? '#f6ffed' : 'transparent',
                border: isBest ? '1px solid #b7eb8f' : 'none',
                borderRadius: '4px',
              }}
            >
              {metric.renderValue(metric.getValue(brand), brand)}
              {isBest && (
                <div style={{ marginTop: 4 }}>
                  <Tag color="green">最佳</Tag>
                </div>
              )}
            </div>
          );
        },
      })),
    ];

    const dataSource = selectedMetrics
      .map(metricKey => {
        const metric = comparisonMetrics.find(m => m.key === metricKey);
        if (!metric) return null;

        const row: any = {
          key: metricKey,
          metric: metric.name,
          icon: metric.icon,
        };

        brands.forEach(brand => {
          row[`brand_${brand.id}`] = metric.getValue(brand);
        });

        return row;
      })
      .filter(Boolean);

    return (
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
      />
    );
  };

  const renderSummaryCards = () => {
    return (
      <Row gutter={16} style={{ marginBottom: 24 }}>
        {brands.map(brand => (
          <Col span={24 / brands.length} key={brand.id}>
            <Card size="small">
              <div style={{ textAlign: 'center' }}>
                <Title level={5}>{brand.name}</Title>
                <div style={{ marginBottom: 12 }}>
                  <Rate
                    disabled
                    value={Number((brand as any).overall_score) || 0}
                    allowHalf
                  />
                  <div>
                    <Text type="secondary">
                      {Number((brand as any).overall_score || 0).toFixed(1)}/5
                    </Text>
                  </div>
                </div>
                <Space direction="vertical" size="small">
                  <Text type="secondary">{brand.headquarters}</Text>
                  <Text type="secondary">
                    成立于 {new Date(brand.founded).getFullYear()}
                  </Text>
                </Space>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SwapOutlined />
          <span>品牌对比 ({brands.length}/3)</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={Math.min(1200, 400 * brands.length)}
      footer={[
        <Button key="close" onClick={onClose}>
          关闭
        </Button>,
      ]}
    >
      {brands.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Text type="secondary">请选择要对比的品牌</Text>
        </div>
      ) : (
        <div>
          {renderSummaryCards()}

          <Divider>详细对比</Divider>

          <div style={{ marginBottom: 16 }}>
            <Text strong>选择对比指标：</Text>
            <div style={{ marginTop: 8 }}>
              <Space wrap>
                {comparisonMetrics.map(metric => (
                  <Tag.CheckableTag
                    key={metric.key}
                    checked={selectedMetrics.includes(metric.key)}
                    onChange={checked => {
                      if (checked) {
                        setSelectedMetrics([...selectedMetrics, metric.key]);
                      } else {
                        setSelectedMetrics(
                          selectedMetrics.filter(k => k !== metric.key)
                        );
                      }
                    }}
                  >
                    {metric.icon} {metric.name}
                  </Tag.CheckableTag>
                ))}
              </Space>
            </div>
          </div>

          {renderComparisonTable()}
        </div>
      )}
    </Modal>
  );
};

export default BrandComparison;
